import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { http } from "../lib/api";
import { Navbar, Footer } from "../components/Brand";
import { AthleteCard } from "../components/AthleteCard";

const HERO =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_faith-athletes-hub/artifacts/hxnm8kkl_Fua.png";

export default function Directory() {
  const [athletes, setAthletes] = useState([]);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Faith Up Athletes | Athlete Roster & Recruiting Profiles";
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await http.get("/athletes", { params: { search, position } });
        setAthletes(data);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, position]);

  const FIXED = ["Defensive Back", "Defensive Line", "Offensive Line", "Tight End"];
  const positions = [
    "All",
    ...FIXED,
    ...new Set(athletes.map((a) => a.position).filter((p) => p && !FIXED.includes(p))),
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden grain border-b border-white/10 bg-[#061224]">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="py-16 sm:py-24 order-1">
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">
              Faith Up Athletes
              <br />
              <span className="text-[#FF5A00]">Spot-Light Profile</span>
            </h1>
            <p className="text-sm sm:text-base text-zinc-300 mt-6 max-w-xl leading-relaxed">
              Official Faith Up Athlete Spot-Light profiles created to highlight our Athletes and to
              show they can compete on the big stage.
            </p>
          </div>
          <div className="relative order-2 -mx-5 sm:-mx-10 lg:mx-0 overflow-hidden h-[320px] sm:h-[460px] lg:h-[560px]">
            <img
              src={HERO}
              alt="Faith Up Athletes player"
              className="w-full h-full object-cover object-top scale-[1.12] origin-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#061224] via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#061224] lg:via-[#061224]/20 lg:to-transparent" />
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-12">
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end justify-between mb-6">
          <div>
            <p className="label-mono">Roster</p>
            <h2 className="font-display text-4xl sm:text-5xl whitespace-nowrap">
              {athletes.length} Athlete{athletes.length === 1 ? "" : "s"}
            </h2>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="roster-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, team, position"
              className="w-full bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none py-3 pl-10 pr-4 text-sm"
            />
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-10">
          {positions.slice(0, 12).map((p) => (
            <button
              key={p}
              data-testid={`filter-position-${p}`}
              onClick={() => setPosition(p)}
              className={`whitespace-nowrap px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] border transition-colors ${
                position === p
                  ? "bg-[#FF5A00] border-[#FF5A00] text-white"
                  : "border-white/20 text-zinc-400 hover:border-white"
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="label-mono" data-testid="roster-loading">Loading roster…</p>
        ) : athletes.length === 0 ? (
          <div
            data-testid="roster-empty-state"
            className="border border-white/10 bg-[#111111] p-14 text-center"
          >
            <h3 className="font-display text-3xl">
              {position !== "All" || search ? "No matching athletes" : "No athletes published yet"}
            </h3>
            <p className="text-sm text-zinc-400 mt-3">
              {position !== "All" || search
                ? "Try another position tab or clear your search."
                : "Admins can sign in and add the first athlete profile."}
            </p>
          </div>
        ) : (
          <div
            data-testid="roster-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4"
          >
            {athletes.map((a, i) => (
              <AthleteCard key={a.athlete_id} athlete={a} index={i} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
